import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BellAlertModal from '../components/BellAlertModal';
import { isStaffAppPath } from '../utils/staffPaths';
import { isSoundEnabled, setSoundEnabled, playAlertSound, unlockAudioOnFirstGesture } from '../utils/alertSound';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const POLL_MS = 2000;
const MAX_BACKOFF_MS = 15000;
const POPUP_TYPES = ['task_assignment', 'lead_assignment', 'client_assignment', 'task_approval', 'task_mention', 'followup_assigned', 'followup_due', 'followup_overdue', 'followup_digest'];

const KEYFRAMES = `
  @keyframes badge-blink { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.3); opacity: 0.7; } }
  @keyframes bell-ring {
    0% { transform: rotate(0deg); } 10% { transform: rotate(18deg); } 25% { transform: rotate(-16deg); }
    40% { transform: rotate(12deg); } 55% { transform: rotate(-10deg); } 70% { transform: rotate(6deg); }
    85% { transform: rotate(-4deg); } 100% { transform: rotate(0deg); }
  }
`;

const NOOP_VALUE = {
  unreadCount: 0, notifications: [], bellRinging: false,
  markAllRead: async () => {}, markOneRead: async () => {}, clearAll: async () => {},
  soundOn: true, toggleSound: () => {},
};

const NotificationsContext = createContext(null);
export const useNotifications = () => useContext(NotificationsContext) || NOOP_VALUE;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// One notification poller for the whole signed-in staff app (Dashboard, every
// workspace, the phone shell). It checks every ~2s for rows newer than the last
// id it saw, so an assignment shows up within a couple of seconds on any page.
export function NotificationsProvider({ children }) {
  const { pathname } = useLocation();
  const active = isStaffAppPath(pathname);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [popupItems, setPopupItems] = useState([]);
  const [bellRinging, setBellRinging] = useState(false);
  const [soundOn, setSoundOn] = useState(isSoundEnabled);

  const lastIdRef = useRef(null); // highest notification id seen; null = not loaded yet
  const tokenRef = useRef(null);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const failuresRef = useRef(0);
  const ringTimerRef = useRef(null);

  const resetState = useCallback(() => {
    lastIdRef.current = null;
    setUnreadCount(0);
    setNotifications([]);
    setPopupItems([]);
  }, []);

  const poll = useCallback(async () => {
    if (!activeRef.current) return;
    const token = localStorage.getItem('token');
    if (!token) {
      if (tokenRef.current) { tokenRef.current = null; resetState(); }
      return;
    }
    if (token !== tokenRef.current) { // a different login: start clean, no popup for old items
      tokenRef.current = token;
      resetState();
    }

    const after = lastIdRef.current;
    const res = await fetch(`${BASE}/api/notifications/poll${after != null ? `?after=${after}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`notifications poll ${res.status}`);
    const data = await res.json();
    const items = Array.isArray(data.items) ? data.items : [];
    setUnreadCount(Number(data.count) || 0);

    if (after == null) { // first load: fill the list, never pop up for what was already there
      setNotifications(items);
      lastIdRef.current = items.length ? Math.max(...items.map(n => n.id)) : 0;
      return;
    }
    if (items.length === 0) return;

    lastIdRef.current = Math.max(after, ...items.map(n => n.id));
    const newestFirst = items.slice().reverse();
    setNotifications(prev => [...newestFirst, ...prev].slice(0, 50));

    const fresh = newestFirst.filter(n => !n.is_read && POPUP_TYPES.includes(n.type));
    if (fresh.length > 0) {
      setPopupItems(prev => [...fresh, ...prev]);
      playAlertSound();
      setBellRinging(true);
      clearTimeout(ringTimerRef.current);
      ringTimerRef.current = setTimeout(() => setBellRinging(false), 2500);
    }
  }, [resetState]);

  useEffect(() => unlockAudioOnFirstGesture(), []);

  useEffect(() => {
    let stopped = false;

    const schedule = (delay) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(tick, delay);
    };
    const tick = async () => {
      if (stopped) return;
      if (inFlightRef.current) { schedule(POLL_MS); return; }
      inFlightRef.current = true;
      try { await poll(); failuresRef.current = 0; }
      catch (_) { failuresRef.current = Math.min(failuresRef.current + 1, 4); }
      finally { inFlightRef.current = false; }
      if (!stopped) schedule(Math.min(POLL_MS * 2 ** failuresRef.current, MAX_BACKOFF_MS));
    };

    tick();

    // Background tabs get their timers throttled by the browser, so catch up the
    // instant the tab/window is back in front or the network returns.
    const wake = () => { if (document.visibilityState !== 'hidden') schedule(0); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    return () => {
      stopped = true;
      clearTimeout(timerRef.current);
      clearTimeout(ringTimerRef.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
    };
  }, [poll]);

  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${BASE}/api/notifications/read-all`, { method: 'PUT', headers: authHeaders() });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setPopupItems([]);
    } catch (_) {}
  }, []);

  const clearAll = useCallback(async () => {
    try {
      await fetch(`${BASE}/api/notifications/read-all`, { method: 'PUT', headers: authHeaders() });
      setUnreadCount(0);
      setNotifications([]);
      setPopupItems([]);
    } catch (_) {}
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await fetch(`${BASE}/api/notifications/${id}/read`, { method: 'PUT', headers: authHeaders() });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
      setPopupItems(prev => prev.filter(n => n.id !== id));
    } catch (_) {}
  }, []);

  // Turning the sound on plays a preview, so the person hears what they just enabled.
  const toggleSound = useCallback(() => {
    setSoundOn(prev => {
      const next = !prev;
      setSoundEnabled(next);
      if (next) playAlertSound({ force: true });
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    unreadCount, notifications, bellRinging, markAllRead, markOneRead, clearAll, soundOn, toggleSound,
  }), [unreadCount, notifications, bellRinging, markAllRead, markOneRead, clearAll, soundOn, toggleSound]);

  return (
    <NotificationsContext.Provider value={value}>
      <style>{KEYFRAMES}</style>
      {children}
      {active && popupItems.length > 0 && (
        <BellAlertModal
          notifications={popupItems}
          onGotIt={markAllRead}
          onClose={() => setPopupItems([])} />
      )}
    </NotificationsContext.Provider>
  );
}
